import { motion, AnimatePresence } from 'framer-motion'
import type { RadarBubbleLesson } from '@/services/api/teacher'
import { useAnimationConfig } from '../radar/hooks/useReducedMotion'
import { GlassTooltipContent } from '../radar/ui/GlassTooltipContent'
import '../radar/styles/radar-tokens.css'

interface RequestListTooltipProps {
  lessonDetails: RadarBubbleLesson[]
  visible: boolean
  statusLabel?: string
  statusColor?: string
  date?: string
  hoursUntilLesson?: number
  effectiveLessonCount?: number
  originalLessonCount?: number
}

export function RequestListTooltip({
  lessonDetails,
  visible,
  statusLabel,
  statusColor,
  date,
  hoursUntilLesson,
  effectiveLessonCount,
  originalLessonCount,
}: RequestListTooltipProps) {
  const animConfig = useAnimationConfig()

  if (!lessonDetails || lessonDetails.length === 0) return null

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('de-CH')
  }

  const hasMultipleLessons = lessonDetails.length > 1
  const singleLesson = !hasMultipleLessons ? lessonDetails[0] : null

  const countLabel = hasMultipleLessons
    ? ` · ${lessonDetails.length} Lektionen`
    : singleLesson?.time_slot_name
      ? ` ${singleLesson.time_slot_name}`
      : undefined

  const partialLabel =
    effectiveLessonCount != null &&
    originalLessonCount != null &&
    effectiveLessonCount !== originalLessonCount
      ? `${effectiveLessonCount}/${originalLessonCount} offen`
      : undefined

  return (
    <div className="absolute inset-0 z-10 pointer-events-none">
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30,
              duration: animConfig.reducedMotion ? 0 : undefined,
            }}
            className="absolute top-full left-0 mt-2 w-auto min-w-[200px] p-3 rounded-xl border z-50 pointer-events-none"
            style={{
              background: 'var(--glass-tooltip-bg)',
              backdropFilter: 'blur(var(--glass-tooltip-blur))',
              WebkitBackdropFilter: 'blur(var(--glass-tooltip-blur))',
              borderColor: 'var(--glass-tooltip-border)',
              boxShadow: 'var(--glass-tooltip-shadow)',
            }}
          >
            <GlassTooltipContent
              dotColor={statusColor || '#94a3b8'}
              dotVariant="solid"
              headerLabel="Vertretung"
              dateLabel={formatDate(date)}
              countLabel={countLabel}
              lessons={lessonDetails}
              statusLabel={statusLabel}
              hoursLabel={hoursUntilLesson != null ? `${hoursUntilLesson.toFixed(1)}h` : undefined}
              partialLabel={partialLabel}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
